import { Component, OnDestroy, OnInit } from '@angular/core';
import { ErrorSeverity } from '@common/knownTypes/ErrorSeverity';
import { ShipmentTypes } from '@common/knownTypes/ShipmentTypes';
import { ImportSummary, ValidationOutcomeRow } from '@common/models/validation-outcomes.model';
import { TranslateService } from '@ngx-translate/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-validation-modal',
    templateUrl: './validation-modal.component.html',
    styleUrls: ['./validation-modal.component.scss'],
    standalone: false
})
export class ValidationModalComponent implements OnDestroy, OnInit {
    public title: string;
    public message: string;
    public rows: ValidationOutcomeRow[] = [];
    public filteredRows: ValidationOutcomeRow[] = [];
    public shipmentTypeId: string;
    public forExcel: boolean;
    public itemHeader: boolean;
    public showSubtitle: boolean;
    public firstColumnHeader: string;
    public secondColumnHeader: string;
    public emptyStateText: string;
    public closeButtonName: string;
    public isBusy = false;
    public summary: ImportSummary | null;
    public filterSeverity: ErrorSeverity = ErrorSeverity.All;
    public searchText = '';
    public readonly severityCodeListValues = Object.values(ErrorSeverity).map((value) => ({
        id: value,
        name: value
    }));

    public destroyed$ = new Subject<void>();

    constructor(
        public activeModal: DialogRef,
        private translateService: TranslateService
    ) {}

    get hasRows(): boolean {
        return (this.rows || []).length > 0;
    }

    get shouldShowSubtitle(): boolean {
        return this.showSubtitle ?? !this.forExcel;
    }

    get subtitle(): string {
        if (!this.shipmentTypeId) return this.translateService.instant('Following items were not added');

        return this.shipmentTypeId === ShipmentTypes.Non
            ? this.translateService.instant('Following items were not added')
            : this.shipmentTypeId === ShipmentTypes.Roro
            ? this.translateService.instant('Following vehicles were not added')
            : this.translateService.instant('Following containers were not added');
    }

    get columnHeader(): string {
        if (this.firstColumnHeader) return this.firstColumnHeader;

        if (this.itemHeader) return this.translateService.instant('Item');

        if (!this.shipmentTypeId) return this.translateService.instant('Container Number');

        return this.shipmentTypeId === ShipmentTypes.Non
            ? this.translateService.instant('B/L Nbr.')
            : this.shipmentTypeId === ShipmentTypes.Roro
            ? this.translateService.instant('Vehicle Number')
            : this.translateService.instant('Container Number');
    }

    get valueHeader(): string {
        return this.secondColumnHeader || this.translateService.instant('Reason');
    }

    get noRowsText(): string {
        return this.emptyStateText || this.translateService.instant('No records match the current filter or search.');
    }

    getSeverityClass(severity?: string): string | null {
        if (!severity) return null;

        switch (severity.toLowerCase()) {
            case 'warning':
                return 'text-warning';
            case 'info':
                return 'text-info';
            case 'error':
                return 'text-danger';
            default:
                return null;
        }
    }

    getSeverityIcon(severity?: string): any {
        if (!severity) return null;

        switch (severity.toLowerCase()) {
            case 'warning':
                return ['fas', 'exclamation-triangle'];
            case 'info':
                return ['fas', 'info-circle'];
            case 'error':
                return ['fas', 'times-circle'];
            default:
                return null;
        }
    }

    get noResults(): boolean {
        return this.hasRows && this.filteredRows.length === 0;
    }

    applyFilter(): void {
        let result = this.rows || [];

        if (this.filterSeverity !== ErrorSeverity.All) {
            result = result.filter((row) => (row.severity ?? ErrorSeverity.Error) === this.filterSeverity);
        }

        if (this.searchText?.trim()) {
            const search = this.searchText.trim().toLowerCase();
            result = result.filter((row) => {
                const firstValue = (row.firstValue || '').toLowerCase();
                const secondValue = (row.secondValue || '').toLowerCase();
                return firstValue.includes(search) || secondValue.includes(search);
            });
        }

        this.filteredRows = result;
    }

    ngOnInit(): void {
        this.applyFilter();
    }

    close() {
        this.activeModal.close(null);
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
